// 시트 보기의 키 입력 → 명령 변환과 커서 이동 규칙(순수 함수). 화면은 명령을 실행만 한다.

import {
  isDataRow,
  SheetRange,
  SheetRow,
  sheetRowKey,
} from "./grid-model";

/** 편집 상태. replace = 글자를 쳐서 들어온 편집(화살표가 칸을 옮김), edit = F2·더블클릭(화살표가 커서를 옮김). */
export type EditMode = "idle" | "replace" | "edit";

export interface KeyInput {
  key: string;
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  /** IME 조합 중(`isComposing` 또는 keyCode 229). */
  composing: boolean;
}

export type SheetCommand =
  | { kind: "move"; dRow: number; dCol: number; extend: boolean; jump: boolean }
  | { kind: "enter"; back: boolean }
  | { kind: "tab"; back: boolean }
  | { kind: "edit" }
  | { kind: "cancel" }
  | { kind: "clear" }
  | { kind: "fillDown" }
  | { kind: "undo" }
  | { kind: "redo" }
  | { kind: "selectAll" }
  | { kind: "copy" }
  | { kind: "cut" }
  /** 기본 동작만 막는다 — 조합 중 Tab 이 포커스를 빼 가지 않게. */
  | { kind: "hold" };

const ARROWS: Record<string, [number, number]> = {
  ArrowUp: [-1, 0],
  ArrowDown: [1, 0],
  ArrowLeft: [0, -1],
  ArrowRight: [0, 1],
};

/** 키 하나를 명령으로. null 이면 브라우저 기본 동작(글자 입력·입력칸 안 이동)에 맡긴다. */
export const commandOf = (e: KeyInput, mode: EditMode): SheetCommand | null => {
  // 조합 중에는 Enter·Tab 이 조합 확정만 한다. Tab 은 포커스 이동만 막는다.
  if (e.composing) return e.key === "Tab" ? { kind: "hold" } : null;

  const mod = e.metaKey || e.ctrlKey;
  const arrow = ARROWS[e.key];

  if (e.key === "Enter" && !mod && !e.altKey) return { kind: "enter", back: e.shiftKey };
  if (e.key === "Tab" && !mod && !e.altKey) return { kind: "tab", back: e.shiftKey };
  if (e.key === "Escape") return { kind: "cancel" };
  if (e.key === "F2") return mode === "edit" ? null : { kind: "edit" };

  if (mode === "edit") return null;
  if (mode === "replace") {
    if (arrow && !mod && !e.altKey) {
      return { kind: "move", dRow: arrow[0], dCol: arrow[1], extend: e.shiftKey, jump: false };
    }
    return null;
  }

  if (arrow && !e.altKey) {
    return { kind: "move", dRow: arrow[0], dCol: arrow[1], extend: e.shiftKey, jump: mod };
  }
  if ((e.key === "Delete" || e.key === "Backspace") && !mod) return { kind: "clear" };
  if (!mod || e.altKey) return null;

  switch (e.key.toLowerCase()) {
    case "d":
      return { kind: "fillDown" };
    case "z":
      return e.shiftKey ? { kind: "redo" } : { kind: "undo" };
    case "y":
      return { kind: "redo" };
    case "a":
      return { kind: "selectAll" };
    case "c":
      return { kind: "copy" };
    case "x":
      return { kind: "cut" };
    default:
      return null;
  }
};

const MOVE_KEYS = new Set(["Enter", "Tab", "Home", "End", "PageUp", "PageDown", "F2"]);

/** 커서를 옮기거나 편집을 여는 키인가 — 이때만 칸이 보이게 표를 민다(Shift·⌘ 단독·편집 중 글자는 아니다). */
export const wantsScroll = (e: KeyInput, mode: EditMode = "idle"): boolean =>
  e.key in ARROWS ||
  MOVE_KEYS.has(e.key) ||
  (e.key.length === 1 && !e.metaKey && !e.ctrlKey && mode === "idle");

/* ── 한글 조합 중 Enter·Tab ──────────────────────────────────── */

/** 조합을 끝낸 Enter·Tab 이 한 번 더 올 수 있는 시간(ms). */
export const IME_ECHO_MS = 100;
/** 조합 Enter 를 실행한 뒤 이 시간(ms) 안의 Enter 는 조합 신호가 없어도 메아리로 삼킨다. */
export const IME_ECHO_HARD_MS = 50;

// 조합 중 누른 Enter·Tab 을 조합이 끝난 뒤 한 번만 실행하려고 들고 있는 상태.
export interface ImeGate {
  pending: KeyInput | null;
  firedKey: string | null;
  firedAt: number;
  endedAt: number;
  /** 조합 끝에 Enter 를 실행한 시각. */
  enterFiredAt: number;
}

export const initImeGate = (): ImeGate => ({
  pending: null,
  firedKey: null,
  firedAt: Number.NEGATIVE_INFINITY,
  endedAt: Number.NEGATIVE_INFINITY,
  enterFiredAt: Number.NEGATIVE_INFINITY,
});

/** composing = 조합 중(`isComposing`·compositionstart 이후), is229 = keyCode 229(Safari 는 조합을 끝낸 뒤에도 이 값). */
export interface GateKey extends KeyInput {
  is229: boolean;
}

/** run = 조합 없이 처리 · pass = 원래대로(229 는 조합으로) · defer = 조합 끝에 실행 · swallow = 방금 실행한 키의 메아리. */
export type GateDecision = "run" | "pass" | "defer" | "swallow";

const isConfirmKey = (e: KeyInput): boolean =>
  (e.key === "Enter" || e.key === "Tab") && !e.metaKey && !e.ctrlKey && !e.altKey;

/** keydown 하나를 조합 상태에 비춰 본다 — 조합 중 Enter 한 번으로 확정과 이동이 같이 되게. */
export const gateKeyDown = (
  gate: ImeGate,
  e: GateKey,
  now: number
): { gate: ImeGate; decision: GateDecision } => {
  if (!isConfirmKey(e)) return { gate, decision: "pass" };
  if (e.key === "Enter" && now - gate.enterFiredAt < IME_ECHO_HARD_MS) {
    return { gate: { ...gate, firedKey: null }, decision: "swallow" };
  }
  // 메아리는 조합 신호(229·isComposing)를 단 것만 — 창 안이라도 진짜 Enter 는 통과한다.
  if (gate.firedKey === e.key && now - gate.firedAt < IME_ECHO_MS && (e.is229 || e.composing)) {
    return { gate: { ...gate, firedKey: null }, decision: "swallow" };
  }
  if (e.composing) {
    return { gate: { ...gate, pending: { ...e, composing: false } }, decision: "defer" };
  }
  if (e.is229) {
    return now - gate.endedAt < IME_ECHO_MS
      ? { gate: { ...gate, endedAt: Number.NEGATIVE_INFINITY }, decision: "run" }
      : { gate, decision: "pass" };
  }
  return { gate, decision: "run" };
};

/** 조합이 시작되면 들고 있던 키를 버린다. */
export const startComposition = (gate: ImeGate): ImeGate => ({ ...gate, pending: null });

/** 편집 취소·blur·클릭·Esc — 조합 끝에 실행하려고 들고 있던 키를 버린다. */
export const clearPending = (gate: ImeGate): ImeGate =>
  gate.pending === null ? gate : { ...gate, pending: null };

/** 조합이 끝났다 — 들고 있던 키를 돌려주고, 뒤따르는 같은 키는 삼키도록 적어 둔다. */
export const endComposition = (
  gate: ImeGate,
  now: number
): { gate: ImeGate; run: KeyInput | null } => {
  const run = gate.pending;
  return {
    gate: {
      pending: null,
      endedAt: run ? Number.NEGATIVE_INFINITY : now,
      firedKey: run ? run.key : gate.firedKey,
      firedAt: run ? now : gate.firedAt,
      enterFiredAt: run?.key === "Enter" ? now : gate.enterFiredAt,
    },
    run,
  };
};

/* ── 붙여넣기 뒤 선택 ──────────────────────────────────────── */

/** 커서·앵커 자리. 행은 키로 따라가고, 키가 없거나 사라지면 index 근처로 떨어진다. */
export interface CellPos {
  key: string | null;
  index: number;
  col: number;
}

/** 붙인 범위의 커서(왼쪽 위)·앵커(오른쪽 아래). 새로 생긴 행(`freshFrom` 부터)은 옛 행의 키를 빌리지 않고 자리로만 가리킨다. */
export const pasteSelection = (
  rows: SheetRow[],
  target: SheetRange,
  freshFrom: number | null
): { cursor: CellPos; anchor: CellPos } => {
  const at = (index: number, col: number): CellPos => {
    const row = rows[index];
    const fresh = freshFrom !== null && index >= freshFrom;
    return { key: row && !fresh ? sheetRowKey(row) : null, index, col };
  };
  return { cursor: at(target.top, target.left), anchor: at(target.bottom, target.right) };
};

/* ── 행 이동 ────────────────────────────────────────────────── */

/** `from` 에서 `delta` 방향으로 가장 가까운 항목 행. 없으면 `from` 그대로. jump 면 그 방향 끝. */
export const moveRow = (
  rows: SheetRow[],
  from: number,
  delta: number,
  jump = false
): number => {
  if (delta === 0) return from;
  const step = delta > 0 ? 1 : -1;
  let found = from;
  let remaining = jump ? Number.POSITIVE_INFINITY : Math.abs(delta);
  for (let i = from + step; i >= 0 && i < rows.length && remaining > 0; i += step) {
    if (isDataRow(rows[i])) {
      found = i;
      remaining -= 1;
    }
  }
  return found;
};

/** 가장 가까운 항목 행 — 아래를 먼저 본다. 항목 행이 하나도 없으면 -1. */
export const nearestDataRow = (rows: SheetRow[], index: number): number => {
  if (rows.length === 0) return -1;
  const at = Math.max(0, Math.min(Math.floor(index), rows.length - 1));
  for (let i = at; i < rows.length; i += 1) if (isDataRow(rows[i])) return i;
  for (let i = at - 1; i >= 0; i -= 1) if (isDataRow(rows[i])) return i;
  return -1;
};

export const clampCol = (col: number, cols: number): number =>
  Math.max(0, Math.min(col, cols - 1));

/** 행의 자리 — 키로 찾고, 지워졌으면 마지막으로 알던 자리에서 가장 가까운 항목 행. */
export const resolveRow = (
  rows: SheetRow[],
  indexByKey: ReadonlyMap<string, number>,
  pos: { key: string | null; index: number }
): number => {
  const byKey = pos.key === null ? undefined : indexByKey.get(pos.key);
  return byKey !== undefined && isDataRow(rows[byKey]) ? byKey : nearestDataRow(rows, pos.index);
};

/** 그 공종의 마지막으로 보이는 행인지 — 다음 행이 없거나 다음 공종 머리 줄. */
export const isLastInTrade = (rows: SheetRow[], index: number): boolean =>
  isDataRow(rows[index]) && !isDataRow(rows[index + 1]);

/** 항목 행 `index` 아래(그 SET 자식 다음)에 새 줄이 설 자리의 화면 행 번호. */
export const insertPositionBelow = (rows: SheetRow[], index: number): number => {
  let i = index + 1;
  while (i < rows.length && rows[i].kind === "child") i += 1;
  return i;
};

/** 채우기 범위. 항목 행이 하나뿐이면 같은 공종의 바로 위 항목 행까지 넓힌다(엑셀 ⌘D). 넓힐 곳이 없으면 null. */
export const fillRangeOf = (rows: SheetRow[], range: SheetRange): SheetRange | null => {
  const dataCount = rows.slice(range.top, range.bottom + 1).filter(isDataRow).length;
  if (dataCount >= 2) return range;
  if (dataCount === 0) return null;
  const above = range.top - 1;
  if (!isDataRow(rows[above])) return null;
  return { ...range, top: above };
};
